
import { useCallback, useRef } from 'react';

interface GTMPurchaseData {
  transaction_id: string;
  value: number;
  currency: string;
  items: Array<{
    item_name: string;
    item_variant: string;
    price: number;
    quantity: number;
  }>;
  user_journey_completed: boolean;
  plan_selected: string;
  medication_selected: string;
  promo_applied: boolean;
}

export const useGTMTracking = () => {
  const firedEvents = useRef<Set<string>>(new Set());

  const trackPurchase = useCallback((data: GTMPurchaseData) => {
    // Check if we've already fired this event for this transaction
    if (firedEvents.current.has(data.transaction_id)) {
      console.log('GTM purchase event already fired for transaction:', data.transaction_id);
      return;
    }

    // Mark this transaction as tracked
    firedEvents.current.add(data.transaction_id);

    // Prepare GTM data
    const gtmData = {
      event: 'purchase',
      ecommerce: {
        purchase: {
          transaction_id: data.transaction_id,
          value: data.value,
          currency: data.currency,
          items: data.items
        }
      },
      user_journey_completed: data.user_journey_completed,
      plan_selected: data.plan_selected,
      medication_selected: data.medication_selected,
      promo_applied: data.promo_applied
    };

    // Send to GTM
    if (window.dataLayer) {
      console.log('Sending GTM purchase event for transaction:', data.transaction_id);
      window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
      window.dataLayer.push(gtmData);
    }

    // Send to Facebook Pixel if available
    if (typeof window.fbq === 'function') {
      try {
        console.log('Sending Facebook Pixel purchase event for transaction:', data.transaction_id);
        window.fbq('track', 'Purchase', {
          value: data.value,
          currency: data.currency,
          content_name: data.items[0]?.item_name,
          content_type: data.items[0]?.item_variant,
          content_ids: [data.transaction_id],
        });
      } catch (error) {
        console.error('Error sending Facebook Pixel event:', error);
      }
    }
  }, []);

  const trackEvent = useCallback((eventName: string, eventData: any) => {
    if (window.dataLayer) {
      console.log('Sending GTM event:', eventName, eventData);
      window.dataLayer.push({
        event: eventName,
        ...eventData
      });
    }
  }, []);

  return {
    trackPurchase,
    trackEvent
  };
};
